import { forwardRef, useEffect, useRef, useState } from 'react';
import { Calendar } from 'react-date-range';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as locales from 'react-date-range/dist/locale';
import classNames from 'classnames/bind';
import { DateTime } from 'luxon';

import styles from './mochi-date-picker.module.scss';
import reactDatePickerOverwriteStyles from './react-date-picker-overwrite.module.scss';

import 'react-date-range/dist/styles.css'; // main css file
import 'react-date-range/dist/theme/default.css'; // theme css file

const cx = classNames.bind(styles);
const reactDatePickerOverwriteCx = classNames.bind(
  reactDatePickerOverwriteStyles,
);

interface DateSelectProps {
  minDate?: Date;
  maxDate?: Date;
  labelFor?: string;
  date: Date | null;
  variant?: 'normal' | 'round' | 'grey';
  placeholder?: string;
  style?: React.CSSProperties;
  format?: string;
  defaultDate?: Date | null;
  errorMessage?: string;
  isDarkMode?: boolean;
  onDateChange: (date: Date) => void;
}

export const MochiDatePicker = forwardRef<HTMLInputElement, DateSelectProps>(
  (
    {
      minDate,
      maxDate,
      labelFor,
      style,
      date,
      defaultDate,
      variant = 'normal',
      format = 'yyyy/MM/dd',
      placeholder = '',
      errorMessage,
      isDarkMode = false,
      onDateChange,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const isInvalid = !!errorMessage;

    const containerClass = cx({
      [variant]: true,
      container: true,
      placeholder: date == null,
      selecting: isOpen,
      invalid: isInvalid,
      'light-mode': !isDarkMode,
      'dark-mode': isDarkMode,
    });

    const errorMessageClass = cx({
      [variant]: true,
      'error-message': true,
    });

    const datePickerClass = reactDatePickerOverwriteCx({
      'date-picker': true,
      'light-mode': !isDarkMode,
      'dark-mode': isDarkMode,
    });

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current == null) return;

        if (!containerRef.current.contains(event.target as Node)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    return (
      <div>
        <div
          style={style}
          className={containerClass}
          ref={containerRef}
          onClick={() => setIsOpen(true)}
        >
          <input
            ref={ref}
            id={labelFor}
            tabIndex={-1}
            placeholder={placeholder}
            type="text"
            readOnly
            defaultValue={
              defaultDate == null
                ? ''
                : DateTime.fromJSDate(defaultDate).toFormat(format)
            }
            value={
              date == null ? '' : DateTime.fromJSDate(date).toFormat(format)
            }
          />
          {isOpen && (
            <Calendar
              minDate={minDate}
              maxDate={maxDate}
              className={`${datePickerClass}`}
              classNames={{
                monthAndYearWrapper:
                  reactDatePickerOverwriteStyles[
                    'date-picker-month-year-wrapper'
                  ],
                prevButton:
                  reactDatePickerOverwriteStyles['date-picker-prev-button'],
                nextButton:
                  reactDatePickerOverwriteStyles['date-picker-next-button'],
                yearPicker:
                  reactDatePickerOverwriteStyles['date-picker-year-picker'],
                monthPicker:
                  reactDatePickerOverwriteStyles['date-picker-month-picker'],
                dayNumber:
                  reactDatePickerOverwriteStyles['date-picker-day-number'],
                dayPassive:
                  reactDatePickerOverwriteStyles['date-picker-day-passive'],
              }}
              locale={locales['zhTW']}
              date={date ?? undefined}
              onChange={(date) => {
                setIsOpen(false);
                onDateChange(date);
              }}
            />
          )}
        </div>
        {errorMessage && (
          <small className={errorMessageClass}>{errorMessage}</small>
        )}
      </div>
    );
  },
);

MochiDatePicker.displayName = 'MochiDatePicker';
